import { Link } from "react-router-dom";
import { Building2, CheckCircle2, User } from "lucide-react";
import type { Lead, LeadStatus } from "../../../lib/clients";
import { EmptyState, fmtDate, fmtMoney, listCard } from "../../ui";
import { NextStepBadge } from "../_shared/NextStep";
import { LeadRowActions } from "./LeadRowActions";
import { LeadSource } from "./LeadSource";
import { LeadStatusMenu } from "./LeadStatusMenu";
import type { T } from "./shared";

/**
 * Name, labels, source, added, value, status - the seven tracks the client
 * register uses, so the two lists in this app line up with each other.
 *
 * Company or person is NOT a column of its own here: the glyph in front of the
 * name already says it, and a word repeating a glyph costs a track the labels
 * need more. The board has no room for labels at all (a 240px card), which is
 * the one thing this view can say that the board cannot.
 */
const COLS = "minmax(0,2.6fr) 1fr 1fr .8fr .85fr 1.05fr 44px";

/**
 * The table: every lead as one row, in the order the endpoint returns them
 * (stage first, newest first inside a stage).
 *
 * The board answers "where is everything", the table answers "what is this
 * one" - a stage is one glance on the board and a scan down a column here, and
 * in exchange the row has room for the labels, the money and the date that a
 * card cannot carry.
 *
 * `LeadsList` and not `LeadsTable`, which is the name the shape asks for:
 * `sections/scan-leads/` already has a `LeadsTable.tsx`, and that screen is a
 * different thing that shares the word (the sites the scanner flagged, not the
 * pipeline). Two files with one name in one app is a search that always returns
 * the wrong half. The clients register next door is `ClientsList.tsx`, so this
 * name is also the one a reader would guess.
 */
export function LeadsList({
  leads,
  t,
  onUpdate,
  onDelete,
  onConvert,
  onPlanStep,
}: {
  leads: Lead[];
  t: T;
  onUpdate: (id: string, status: LeadStatus) => void;
  onDelete: (id: string) => void;
  onConvert: (id: string) => void;
  onPlanStep: (lead: Lead) => void;
}) {
  if (leads.length === 0) {
    return (
      <div className="crm-card">
        <EmptyState icon={User} title={t("leads.empty")} body={t("leads.emptyBody")} />
      </div>
    );
  }

  return (
    // `crm-list-stack`: below 560px the row stops being a grid and folds into a
    // block. A lead row is a name and three markers rather than figures somebody
    // compares down a column, so folding beats sideways scrolling on a phone.
    <div className="crm-card crm-list-stack" style={listCard(COLS)}>
      <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
        <span>{t("leads.name")}</span>
        <span>{t("leads.tags")}</span>
        <span>{t("leads.source")}</span>
        <span>{t("leads.added")}</span>
        <span>{t("leads.value")}</span>
        <span>{t("leads.col.status")}</span>
        <span />
      </div>

      <div className="crm-rows pt-0">
        {leads.map((lead) => (
          <LeadRow
            key={lead.id}
            lead={lead}
            t={t}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onConvert={onConvert}
            onPlanStep={onPlanStep}
          />
        ))}
      </div>
    </div>
  );
}

function LeadRow({
  lead,
  t,
  onUpdate,
  onDelete,
  onConvert,
  onPlanStep,
}: {
  lead: Lead;
  t: T;
  onUpdate: (id: string, status: LeadStatus) => void;
  onDelete: (id: string) => void;
  onConvert: (id: string) => void;
  onPlanStep: (lead: Lead) => void;
}) {
  const Kind = lead.kind === "company" ? Building2 : User;
  // What to write under the name. Whichever contact detail we have, because a
  // lead with no way to reach it is the one fact this row must not hide behind
  // a dash it shares with a lead that simply has no e-mail.
  const line = lead.email ?? lead.phone ?? lead.regCode ?? "-";

  return (
    // A div, not a link: the row ends in menus, and interactive content inside
    // an <a> is invalid HTML and swallows their clicks. The name link stretches
    // an ::after over the whole row instead.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-2.5">
        <Kind className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <Link
            to={`/leads/${lead.id}`}
            className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full"
          >
            {lead.name}
          </Link>
          <span className="crm-prow-s crm-trunc flex items-center gap-2">
            <span className="crm-trunc">{line}</span>
            {/* A converted lead is out of the pipeline and is not asked for a
                next contact - the same rule the board card follows. The chip is
                a button here because the planning dialog is on this page. */}
            {!lead.convertedClientId ? (
              <NextStepBadge nextStepAt={lead.nextStepAt} onPlan={() => onPlanStep(lead)} />
            ) : null}
          </span>
        </span>
      </span>

      {/* Empty rather than a dash. The column head says what is missing, and
          below 560px the head is gone and the row is a block - where a stray
          placeholder dash floats between two real values with nothing left to
          explain it. */}
      <span className="flex min-w-0 flex-wrap items-center gap-1">
        {lead.tags.map((tag) => (
          <span key={tag} className="crm-chip">
            {tag}
          </span>
        ))}
      </span>

      <span className="flex min-w-0 items-center">
        <LeadSource lead={lead} t={t} />
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(lead.createdAt)}</span>
      <span className="crm-tnum text-[13.25px] font-bold">
        {lead.valueCents != null ? fmtMoney(lead.valueCents) : null}
      </span>

      <span className="flex items-center gap-1.5">
        <LeadStatusMenu lead={lead} t={t} onUpdate={onUpdate} />
        {lead.convertedClientId ? (
          <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" aria-label={t("leads.converted")} />
        ) : null}
      </span>

      <LeadRowActions lead={lead} t={t} onDelete={onDelete} onConvert={onConvert} />
    </div>
  );
}
